<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  knowledgeBase: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const isSaving = ref(false);
const name = ref(props.knowledgeBase.name);
const description = ref(props.knowledgeBase.description || '');
const embeddingProvider = ref(props.knowledgeBase.embedding_provider);
const embeddingModel = ref(props.knowledgeBase.embedding_model);
const status = ref(props.knowledgeBase.status);

const accountId = computed(() => store.getters.getCurrentAccountId);

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'voyage', label: 'Voyage AI' },
  { value: 'anthropic', label: 'Anthropic' },
];

const MODELS_BY_PROVIDER = {
  openai: [
    { value: 'text-embedding-3-small', label: 'text-embedding-3-small (recomendado)' },
    { value: 'text-embedding-3-large', label: 'text-embedding-3-large' },
    { value: 'text-embedding-ada-002', label: 'text-embedding-ada-002 (legado)' },
  ],
  voyage: [
    { value: 'voyage-3-lite', label: 'voyage-3-lite (recomendado)' },
    { value: 'voyage-3', label: 'voyage-3' },
    { value: 'voyage-code-3', label: 'voyage-code-3' },
  ],
  anthropic: [],
};

const availableModels = computed(() => MODELS_BY_PROVIDER[embeddingProvider.value] || []);

// Reset model when provider changes
watch(embeddingProvider, newProvider => {
  const models = MODELS_BY_PROVIDER[newProvider] || [];
  if (models.length) embeddingModel.value = models[0].value;
  else embeddingModel.value = '';
});

const save = async () => {
  if (!name.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('knowledgeBases/update', {
      accountId: accountId.value,
      id: props.knowledgeBase.id,
      data: {
        name: name.value.trim(),
        description: description.value.trim(),
        embedding_provider: embeddingProvider.value,
        embedding_model: embeddingModel.value,
        status: status.value,
      },
    });
    useAlert(t('KNOWLEDGE_BASE.SETTINGS_TAB.SAVE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.SETTINGS_TAB.SAVE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="space-y-5 max-w-lg">
    <div>
      <h3 class="text-base font-semibold text-n-slate-12 m-0">
        {{ $t('KNOWLEDGE_BASE.SETTINGS_TAB.TITLE') }}
      </h3>
    </div>

    <!-- Nome -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.FORM.NAME') }} *
      </label>
      <input v-model="name" type="text" class="input" />
    </div>

    <!-- Descrição -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.FORM.DESCRIPTION') }}
      </label>
      <textarea v-model="description" class="input" rows="3" />
    </div>

    <!-- Status -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.SETTINGS_TAB.STATUS') }}
      </label>
      <select v-model="status" class="input">
        <option value="active">{{ $t('KNOWLEDGE_BASE.STATUS.ACTIVE') }}</option>
        <option value="archived">{{ $t('KNOWLEDGE_BASE.STATUS.ARCHIVED') }}</option>
      </select>
    </div>

    <hr class="border-n-weak" />

    <!-- Provider de Embedding -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.SETTINGS_TAB.EMBEDDING_PROVIDER') }}
      </label>
      <select v-model="embeddingProvider" class="input">
        <option v-for="p in PROVIDERS" :key="p.value" :value="p.value">{{ p.label }}</option>
      </select>
    </div>

    <!-- Modelo de Embedding -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.SETTINGS_TAB.EMBEDDING_MODEL') }}
      </label>
      <div v-if="availableModels.length">
        <select v-model="embeddingModel" class="input">
          <option v-for="m in availableModels" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </div>
      <p v-else class="text-xs text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.SETTINGS_TAB.PROVIDER_NOT_AVAILABLE') }}
      </p>
    </div>

    <div class="pt-2">
      <Button
        :label="$t('KNOWLEDGE_BASE.SETTINGS_TAB.SAVE.BUTTON')"
        :is-loading="isSaving"
        :disabled="!name.trim()"
        @click="save"
      />
    </div>
  </div>
</template>
